import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchTimetableEvents, getEnrollments, toggleEnrollment, getCustomEvents, deleteCustomEvent } from '../lib/api';
import { format, startOfWeek, addDays, isSameDay } from 'date-fns';
import WeekNav from '../components/WeekNav';
import EventCard from '../components/EventCard';
import AddEventModal from '../components/AddEventModal';
import { Button } from '../components/ui/button';
import { Plus, RefreshCw, Settings, Calendar, X } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

function groupEventsByDay(events, rangeStart, rangeEnd) {
  const days = {};
  let d = rangeStart;
  while (d <= rangeEnd) {
    days[format(d, 'yyyy-MM-dd')] = [];
    d = addDays(d, 1);
  }
  events.forEach(ev => {
    const date = ev.start ? format(new Date(ev.start), 'yyyy-MM-dd') : null;
    if (date && days[date]) days[date].push(ev);
  });
  Object.keys(days).forEach(k => {
    days[k].sort((a, b) => new Date(a.start) - new Date(b.start));
  });
  return days;
}

// Simple native date picker dialog
function CalendarPicker({ currentDate, onSelect, onClose }) {
  const [value, setValue] = useState(format(currentDate, 'yyyy-MM-dd'));
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-background border border-border rounded-t-2xl sm:rounded-xl p-5 w-full max-w-sm shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-end mb-4">
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <input
          type="date"
          value={value}
          onChange={e => {
            const newValue = e.target.value;
            setValue(newValue);
            if (newValue) {
              onSelect(new Date(newValue + 'T12:00:00'));
              onClose();
            }
          }}
          className="w-full border border-border rounded-lg px-3 py-2.5 bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>
    </div>
  );
}

export default function Dashboard({ onNavigateToSettings }) {
  const { user } = useAuth();
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [customEvents, setCustomEvents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showAll, setShowAll] = useState(() => localStorage.getItem('tu_show_all_subjects') === 'true');

  // Re-sync showAll when navigating back from settings
  useEffect(() => {
    const onStorageChange = () => setShowAll(localStorage.getItem('tu_show_all_subjects') === 'true');
    window.addEventListener('storage', onStorageChange);
    const onFocus = () => setShowAll(localStorage.getItem('tu_show_all_subjects') === 'true');
    window.addEventListener('focus', onFocus);
    return () => { window.removeEventListener('storage', onStorageChange); window.removeEventListener('focus', onFocus); };
  }, []);

  const settings = user?.settings || {};

  // Load a sliding window: 2 weeks around today so changing days doesn't re-fetch
  const getRangeForDay = (day) => {
    const weekStart = startOfWeek(day, { weekStartsOn: 1 });
    return { start: addDays(weekStart, -7), end: addDays(weekStart, 13) };
  };

  // Track which range is currently loaded
  const loadedRangeRef = useRef(null);

  const loadData = useCallback(async (showLoader = true, forceDay = null) => {
    if (!settings.category_type_id || !settings.category_id) return;
    const day = forceDay || selectedDay;
    const range = getRangeForDay(day);
    const rangeKey = `${format(range.start, 'yyyy-MM-dd')}_${format(range.end, 'yyyy-MM-dd')}`;

    // Skip re-fetch if same range already loaded (only when not forced)
    if (!showLoader && loadedRangeRef.current === rangeKey) return;

    if (showLoader) setLoading(true);
    else setRefreshing(true);
    try {
      const [eventsRes, enrollRes, customRes] = await Promise.all([
        fetchTimetableEvents(settings.category_type_id, settings.category_id, range.start.toISOString(), range.end.toISOString()),
        getEnrollments(),
        getCustomEvents(),
      ]);
      setEvents(eventsRes.data.events || []);
      setEnrollments(enrollRes.data || []);
      setCustomEvents(customRes.data || []);
      loadedRangeRef.current = rangeKey;
    } catch (err) {
      console.error('Failed to load timetable', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedDay, settings.category_type_id, settings.category_id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDaySelect = (day) => {
    setSelectedDay(day);
    // If navigating outside current loaded range, trigger a reload
    if (loadedRangeRef.current) {
      const range = getRangeForDay(day);
      const rangeKey = `${format(range.start, 'yyyy-MM-dd')}_${format(range.end, 'yyyy-MM-dd')}`;
      if (rangeKey !== loadedRangeRef.current) {
        // reload silently
        loadData(true, day);
      }
    }
  };

  const handleToggleEnroll = async (eventName, eventType) => {
    try {
      const { data } = await toggleEnrollment(eventName, eventType);
      if (data.enrolled) {
        setEnrollments(prev => [...prev, { event_name: eventName, event_type: eventType }]);
      } else {
        setEnrollments(prev => prev.filter(e => e.event_name !== eventName));
      }
    } catch (err) {
      console.error('Failed to toggle enrollment', err);
    }
  };

  const handleDeleteCustomEvent = async (id) => {
    try {
      await deleteCustomEvent(id);
      setCustomEvents(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error('Failed to delete event', err);
    }
  };

  const isEnrolled = (eventName) => enrollments.some(e => e.event_name === eventName);

  const filteredEvents = showAll
    ? events
    : events.filter(ev => isEnrolled(ev.name));

  const { start: rangeStart, end: rangeEnd } = getRangeForDay(selectedDay);
  const dayGroups = groupEventsByDay(filteredEvents, rangeStart, rangeEnd);
  const selectedDayKey = format(selectedDay, 'yyyy-MM-dd');
  const dayEvents = dayGroups[selectedDayKey] || [];
  const dayCustomEvents = customEvents.filter(e => e.date === selectedDayKey);
  const totalCount = dayEvents.length + dayCustomEvents.length;

  return (
    <div className="min-h-screen" data-testid="dashboard">
      <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold font-['Outfit'] tracking-tight leading-none">Timetable</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground truncate">
                {settings.category_name || 'Master RCS SS'}
              </p>
              {showAll && (
                <span className="text-[9px] font-bold uppercase tracking-wider bg-primary/15 text-primary px-1.5 py-0.5 rounded-sm flex-shrink-0">
                  All
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            <Button variant="ghost" size="icon" onClick={() => setShowAddModal(true)} className="h-8 w-8 rounded-sm" data-testid="add-event-button">
              <Plus className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setShowCalendar(true)} className="h-8 w-8 rounded-sm" data-testid="calendar-button">
              <Calendar className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => loadData(false)} disabled={refreshing} className="h-8 w-8 rounded-sm" data-testid="refresh-button">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="icon" onClick={onNavigateToSettings} className="h-8 w-8 rounded-sm" data-testid="settings-button">
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-5 pb-24">
        {/* Minimal swipeable day navigator */}
        <WeekNav
          selectedDay={selectedDay}
          onDaySelect={handleDaySelect}
          onOpenCalendar={() => setShowCalendar(true)}
        />

        {/* Divider + event count */}
        <div className="flex items-center justify-between mt-5 mb-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-3">
            {loading ? '—' : `${totalCount} event${totalCount !== 1 ? 's' : ''}`}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {loading ? (
          <div className="space-y-3 mt-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 rounded-sm" />)}
          </div>
        ) : (
          <div className="space-y-2 stagger-children">
            {dayEvents.map((ev, idx) => (
              <EventCard
                key={`${ev.event_id}-${idx}`}
                event={ev}
                isEnrolled={isEnrolled(ev.name)}
                onToggleEnroll={handleToggleEnroll}
                showEnrolledDot={!showAll}
              />
            ))}
            {dayCustomEvents.map((ev) => (
              <div key={ev.id} className="relative group">
                <EventCard event={ev} isCustom showEnrolledDot={false} />
                <button
                  onClick={() => handleDeleteCustomEvent(ev.id)}
                  data-testid={`delete-custom-event-${ev.id}`}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-xs text-destructive hover:text-destructive/80 transition-opacity duration-150"
                >
                  Delete
                </button>
              </div>
            ))}
            {totalCount === 0 && (
              <div className="text-center py-16" data-testid="empty-day">
                <Calendar className="w-10 h-10 mx-auto text-muted-foreground/25 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">No classes today</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Enjoy your free time!</p>
              </div>
            )}
          </div>
        )}
      </main>



      <AddEventModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        selectedDate={selectedDay}
        onEventCreated={(newEvent) => setCustomEvents(prev => [...prev, newEvent])}
      />

      {showCalendar && (
        <CalendarPicker
          currentDate={selectedDay}
          onSelect={handleDaySelect}
          onClose={() => setShowCalendar(false)}
        />
      )}
    </div>
  );
}
